import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import '../styles/AdminPage.css';

// Simple debounce function to limit API calls
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const AdminPage = ({ apiBaseUrl }) => {
  const { isAuthenticated, userRoles } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [inviteLink, setInviteLink] = useState('');
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', buttons: [], icon: null });
  const [tasks, setTasks] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState({}); // Track dropdown states

  useEffect(() => {
    if (!isAuthenticated || !userRoles.includes('admin')) return;

    // Fetch users and roles
    fetch(`${apiBaseUrl}/users/manage`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        setUsers(data.users);
        setRoles(data.roles);
      })
      .catch(err => console.error('Error fetching users/roles:', err));

    // Fetch scheduled tasks
    fetch(`${apiBaseUrl}/scheduled_tasks`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => setTasks(data.tasks))
      .catch(err => console.error('Error fetching tasks:', err));
  }, [apiBaseUrl, isAuthenticated, userRoles]);

  const showModal = (title, message, buttons, icon = null) => {
    setModal({ isOpen: true, title, message, buttons, icon });
  };

  const closeModal = () => setModal({ ...modal, isOpen: false });

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const selectedRoles = formData.getAll('roles');

    try {
      const res = await fetch(`${apiBaseUrl}/users/create_invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ roles: selectedRoles }),
      });
      const data = await res.json();
      const frontendUrl = `${window.location.origin}/register/${data.token}`;
      setInviteLink(frontendUrl);
      showModal(
        'Invite Created',
        `Invite link: ${frontendUrl}`,
        [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
        <img src="/static/icons/check-circle-solid.svg" alt="Success" className="modal-icon" />
      );
    } catch (err) {
      showModal(
        'Error',
        'Failed to create invite',
        [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
        <img src="/static/icons/xmark-circle-solid.svg" alt="Error" className="modal-icon" />
      );
      console.error('Error creating invite:', err);
    }
  };

  const handleChangeRoles = async (userId, selectedRoleIds) => {
    try {
      const res = await fetch(`${apiBaseUrl}/users/change_roles/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ roles: selectedRoleIds }),
      });
      const data = await res.json();
      if (data.message === 'Roles updated') {
        setUsers(prev =>
          prev.map(user =>
            user.id === userId ? { ...user, roles: roles.filter(r => selectedRoleIds.includes(String(r.id))).map(r => r.name) } : user
          )
        );
        showModal(
          'Success',
          'Roles updated successfully',
          [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
          <img src="/static/icons/check-circle-solid.svg" alt="Success" className="modal-icon" />
        );
      }
    } catch (err) {
      showModal(
        'Error',
        'Failed to update roles',
        [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
        <img src="/static/icons/xmark-circle-solid.svg" alt="Error" className="modal-icon" />
      );
      console.error('Error updating roles:', err);
    }
  };

  const handleSendResetInvite = async (userId) => {
    try {
      const res = await fetch(`${apiBaseUrl}/users/send_reset_invite/${userId}`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      const frontendUrl = `${window.location.origin}/register/${data.token}`;
      showModal(
        'Reset Link Sent',
        `Reset link: ${frontendUrl}`,
        [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
        <img src="/static/icons/check-circle-solid.svg" alt="Success" className="modal-icon" />
      );
    } catch (err) {
      showModal(
        'Error',
        'Failed to send reset link',
        [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
        <img src="/static/icons/xmark-circle-solid.svg" alt="Error" className="modal-icon" />
      );
      console.error('Error sending reset invite:', err);
    }
  };

  const handleRefreshMedia = async (mediaType) => {
    showModal(
      'Warning: Refresh Media',
      `Are you sure you want to refresh all ${mediaType} media? This action may take a while and consume API resources.`,
      [
        { label: 'Cancel', class: 'btn btn-secondary', action: closeModal },
        {
          label: 'Confirm',
          class: 'btn btn-primary',
          action: async () => {
            try {
              const res = await fetch(`${apiBaseUrl}/refresh_all_media/${mediaType}`, {
                method: 'POST',
                credentials: 'include',
              });
              const data = await res.json();
              showModal(
                data.success ? 'Success' : 'Error',
                data.message,
                [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
                <img
                  src={`/static/icons/${data.success ? 'check-circle-solid' : 'xmark-circle-solid'}.svg`}
                  alt={data.success ? 'Success' : 'Error'}
                  className="modal-icon"
                />
              );
            } catch (err) {
              showModal(
                'Error',
                'Failed to refresh media',
                [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
                <img src="/static/icons/xmark-circle-solid.svg" alt="Error" className="modal-icon" />
              );
              console.error('Error refreshing media:', err);
            }
          },
        },
      ],
      <img src="/static/icons/exclamation-triangle-solid.svg" alt="Warning" className="modal-icon" />
    );
  };

  const handleRunTask = async (taskName) => {
    try {
      const res = await fetch(`${apiBaseUrl}/tasks/execute/${taskName}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        // Update last_executed for the task
        setTasks(prev =>
          prev.map(t =>
            t.task_name === taskName ? { ...t, last_executed: new Date().toISOString() } : t
          )
        );
        showModal(
          'Success',
          data.message,
          [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
          <img src="/static/icons/check-circle-solid.svg" alt="Success" className="modal-icon" />
        );
      } else {
        throw new Error(data.error || 'Failed to execute task');
      }
    } catch (err) {
      showModal(
        'Error',
        `Failed to execute task: ${err.message}`,
        [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
        <img src="/static/icons/xmark-circle-solid.svg" alt="Error" className="modal-icon" />
      );
      console.error('Error executing task:', err);
    }
  };

  const handleUpdateTask = async (taskId, updatedFields) => {
    try {
      const res = await fetch(`${apiBaseUrl}/scheduled_tasks/update/${taskId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updatedFields),
      });
      const data = await res.json();
      if (data.message === 'Task updated successfully') {
        setTasks(prev =>
          prev.map(t =>
            t.id === taskId ? { ...t, ...updatedFields } : t
          )
        );
        showModal(
          'Success',
          'Task updated successfully',
          [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
          <img src="/static/icons/check-circle-solid.svg" alt="Success" className="modal-icon" />
        );
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      showModal(
        'Error',
        `Failed to update task: ${err.message}`,
        [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
        <img src="/static/icons/xmark-circle-solid.svg" alt="Error" className="modal-icon" />
      );
      console.error('Error updating task:', err);
    }
  };

  // Debounced version of handleUpdateTask with 2-second delay
  const debouncedUpdateTask = useRef(debounce((taskId, fields) => handleUpdateTask(taskId, fields), 2000)).current;

  const toggleDropdown = (taskId, type) => {
    setDropdownOpen(prev => ({
      ...prev,
      [`${taskId}-${type}`]: !prev[`${taskId}-${type}`]
    }));
  };

  const handleCheckboxChange = (taskId, type, value, checked) => {
    setTasks(prev =>
      prev.map(t => {
        if (t.id !== taskId) return t;
        const currentValues = t[type] || [];
        const newValues = checked
          ? [...currentValues, value]
          : currentValues.filter(v => v !== value);
        const updatedFields = { [type]: newValues.length ? newValues : null };
        // Trigger save immediately
        debouncedUpdateTask(taskId, {
          task_frequency: t.task_frequency,
          preferred_execution_times: t.preferred_execution_times,
          preferred_execution_days: t.preferred_execution_days,
          ...updatedFields
        });
        return { ...t, ...updatedFields };
      })
    );
  };

  const formatTaskName = (name) => {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (!isAuthenticated || !userRoles.includes('admin')) {
    return (
      <div className="home-container">
        <h1 className="admin-title">Access Denied</h1>
        <div className="admin-tile">
          <p>You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  const mediaTypes = ['Anime', 'Manga', 'PhysicalGame', 'SteamGame', 'Music'];
  const frequencyOptions = ['0.25', '0.5', '1', '2', '3', '5', '7', '10', '14', '30'];
  const timeOptions = ['06:00', '12:00', '18:00', '00:00'];
  const dayOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="home-container admin-container">
      <h1 className="admin-title">Admin Dashboard</h1>
      <div className="admin-grid">
        {/* Left Column: User Management */}
        <div className="admin-column">
          {/* Invite Generator Tile */}
          <div className="admin-tile">
            <h2 className="admin-tile-title">Create Invite Link</h2>
            <form className="admin-form" onSubmit={handleCreateInvite}>
              <div className="role-checklist">
                {roles.map(role => (
                  <label key={role.id} className="admin-role-label">
                    <input type="checkbox" name="roles" value={role.id} />
                    {role.name}
                  </label>
                ))}
              </div>
              <button type="submit" className="btn btn-secondary">
                Generate Invite
              </button>
            </form>
            {inviteLink && (
              <p className="admin-invite-link">{inviteLink}</p>
            )}
          </div>

          {/* Users List Tile */}
          <div className="admin-tile admin-users-tile">
            <h2 className="admin-tile-title">Manage Users</h2>
            <div className="admin-users-list">
              {users.map(user => (
                <div key={user.id} className="admin-user-card">
                  <div className="admin-user-header">
                    <strong>{user.username}</strong>
                  </div>
                  <div className="role-checklist">
                    {roles.map(role => (
                      <label key={role.id} className="admin-role-label">
                        <input
                          type="checkbox"
                          checked={user.roles.includes(role.name)}
                          onChange={(e) => {
                            const newRoles = e.target.checked
                              ? [...user.roles, role.name]
                              : user.roles.filter(r => r !== role.name);
                            handleChangeRoles(
                              user.id,
                              roles.filter(r => newRoles.includes(r.name)).map(r => String(r.id))
                            );
                          }}
                        />
                        {role.name}
                      </label>
                    ))}
                  </div>
                  <div className="button-group">
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleSendResetInvite(user.id)}
                    >
                      Reset Password
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column: System Management */}
        <div className="admin-column">
          {/* Refresh Media Tile */}
          <div className="admin-tile">
            <h2 className="admin-tile-title">Refresh Media</h2>
            <p>Refresh media data from external APIs. (this will replace any manual edits)</p>
            <div className="admin-media-buttons">
              {mediaTypes.map(type => (
                <button
                  key={type}
                  className="btn btn-secondary"
                  onClick={() => handleRefreshMedia(type)}
                >
                  Refresh {type}
                </button>
              ))}
            </div>
          </div>

          {/* Update Intervals Tile */}
          <div className="admin-tile">
            <h2 className="admin-tile-title">Update Intervals</h2>
            <p>Configure automatic update intervals for scheduled tasks.</p>
            <div className="admin-form">
              <div className="task-list">
                {tasks.map(task => (
                  <div key={task.id} className="task-item">
                    <div className="task-header">
                      <h3 className="task-name">{formatTaskName(task.task_name)}</h3>
                      <div className="task-controls">
                        <select
                          value={task.task_frequency}
                          onChange={(e) => {
                            const newFrequency = e.target.value;
                            setTasks(prev =>
                              prev.map(t =>
                                t.id === task.id ? { ...t, task_frequency: newFrequency } : t
                              )
                            );
                            debouncedUpdateTask(task.id, {
                              task_frequency: newFrequency,
                              preferred_execution_times: task.preferred_execution_times,
                              preferred_execution_days: task.preferred_execution_days
                            });
                          }}
                          className="task-frequency"
                        >
                          {frequencyOptions.map(freq => (
                            <option key={freq} value={freq}>{freq} days</option>
                          ))}
                        </select>
                        <div className="dropdown">
                          <button
                            className="dropdown-toggle"
                            onClick={() => toggleDropdown(task.id, 'times')}
                          >
                            Select Times
                          </button>
                          {dropdownOpen[`${task.id}-times`] && (
                            <div className="dropdown-menu">
                              {timeOptions.map(time => (
                                <label key={time} className="dropdown-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={(task.preferred_execution_times || []).includes(time)}
                                    onChange={(e) => handleCheckboxChange(
                                      task.id,
                                      'preferred_execution_times',
                                      time,
                                      e.target.checked
                                    )}
                                  />
                                  {time}
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="dropdown">
                          <button
                            className="dropdown-toggle"
                            onClick={() => toggleDropdown(task.id, 'days')}
                          >
                            Select Days
                          </button>
                          {dropdownOpen[`${task.id}-days`] && (
                            <div className="dropdown-menu">
                              {dayOptions.map(day => (
                                <label key={day} className="dropdown-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={(task.preferred_execution_days || []).includes(day)}
                                    onChange={(e) => handleCheckboxChange(
                                      task.id,
                                      'preferred_execution_days',
                                      day,
                                      e.target.checked
                                    )}
                                  />
                                  {day}
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                        <div style={{borderLeft:"2px solid #fff6", height:"25px", margin:"5px"}}></div>
                        <button
                          className="dropdown-toggle task-run-button"
                          onClick={() => handleRunTask(task.task_name)}
                          title="Run Task Now"
                        >
                          <img
                            src="/static/icons/play-solid.svg"
                            alt="Run Task"
                            className="task-run-icon"
                          />
                        </button>
                      </div>
                    </div>
                    
                    <p className="task-last-executed">
                      Last executed: {task.last_executed ? new Date(task.last_executed).toLocaleString() : 'Never'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Statistics Tile */}
          <div className="admin-tile">
            <h2 className="admin-tile-title">Statistics</h2>
            <p>View system and user statistics (Placeholder - Coming soon).</p>
          </div>
        </div>
      </div>

      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        buttons={modal.buttons}
        icon={modal.icon}
      />
    </div>
  );
};

export default AdminPage;